import React, { useMemo, useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus } from 'lucide-react';

// Small chip input: comma/enter adds, click X removes.
// Optional `suggestions` shows a dropdown to pick existing values or create new.
// The dropdown is portal-rendered with fixed positioning so it never gets
// clipped by overflow:hidden ancestors, and its scroll is self-contained.
export default function ChipInput({
  value,
  onChange,
  placeholder,
  testid,
  palette = 'cmic',
  suggestions = [],
  allowCreate = true,
}) {
  const [draft, setDraft] = useState('');
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const wrapRef = useRef(null);
  const popRef = useRef(null);
  const chips = useMemo(() => (Array.isArray(value) ? value : []), [value]);
  const hasSuggestions = Array.isArray(suggestions) && suggestions.length > 0;

  const palettes = {
    cmic: 'bg-cmic-50 text-cmic-700 border-cmic-100',
    gray: 'bg-gray-100 text-charcoal-700 border-gray-200',
  };
  const palStyle = palettes[palette] || palettes.cmic;

  const selectedLower = useMemo(
    () => new Set(chips.map((c) => String(c).trim().toLowerCase())),
    [chips],
  );
  const filtered = useMemo(() => {
    const q = draft.trim().toLowerCase();
    return (suggestions || [])
      .filter((s) => !selectedLower.has(String(s).trim().toLowerCase()))
      .filter((s) => (q ? String(s).toLowerCase().includes(q) : true))
      .slice(0, 200);
  }, [suggestions, draft, selectedLower]);

  const draftTrimmed = draft.trim();
  const canCreate = allowCreate && draftTrimmed
    && !selectedLower.has(draftTrimmed.toLowerCase())
    && !(suggestions || []).some((s) => String(s).trim().toLowerCase() === draftTrimmed.toLowerCase());

  const showDropdown = open && hasSuggestions && (filtered.length > 0 || canCreate);

  // Position the popover under the field using viewport coords.
  useLayoutEffect(() => {
    if (!showDropdown || !wrapRef.current) return;
    function place() {
      const rect = wrapRef.current.getBoundingClientRect();
      const width = rect.width;
      const popMax = 260;
      const viewportH = window.innerHeight;
      let top = rect.bottom + 4;
      let openUp = false;
      if (top + popMax > viewportH - 8 && rect.top > popMax + 8) {
        top = rect.top - 4;
        openUp = true;
      }
      setPos({ top, left: rect.left, width, openUp });
    }
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [showDropdown, chips.length, filtered.length]);

  // Close on click outside / ESC.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function add(raw) {
    const parts = raw.split(/[,;\n]/).map((s) => s.trim()).filter(Boolean);
    if (!parts.length) return;
    const next = [...chips];
    for (const p of parts) {
      if (!next.some((c) => String(c).toLowerCase() === p.toLowerCase())) next.push(p);
    }
    onChange(next);
    setDraft('');
  }
  function addOne(v) {
    if (!chips.some((c) => String(c).toLowerCase() === String(v).toLowerCase())) {
      onChange([...chips, v]);
    }
    setDraft('');
  }
  function remove(idx) {
    const next = chips.slice();
    next.splice(idx, 1);
    onChange(next);
  }
  function onKey(e) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      add(draft);
    } else if (e.key === 'Backspace' && !draft && chips.length) {
      remove(chips.length - 1);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div className="relative" ref={wrapRef}>
      <div
        className="flex flex-wrap items-center gap-1.5 w-full rounded-lg border border-gray-200 px-2 py-1.5 focus-within:ring-2 focus-within:ring-cmic-500/30 focus-within:border-cmic-500"
        data-testid={testid}
        onClick={() => hasSuggestions && setOpen(true)}
      >
        {chips.map((c, i) => (
          <span
            key={`${c}-${i}`}
            className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded border ${palStyle}`}
          >
            {c}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); remove(i); }}
              className="hover:opacity-70"
              aria-label={`Quitar ${c}`}
            >
              <X size={11} />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={draft}
          onChange={(e) => { setDraft(e.target.value); if (hasSuggestions) setOpen(true); }}
          onFocus={() => hasSuggestions && setOpen(true)}
          onKeyDown={onKey}
          onBlur={() => { if (!hasSuggestions && draft) add(draft); }}
          placeholder={chips.length ? '' : placeholder}
          className="flex-1 min-w-[120px] outline-none text-sm px-1 py-0.5"
          data-testid={testid ? `${testid}-input` : undefined}
        />
        {draft && !hasSuggestions && (
          <button
            type="button"
            onClick={() => add(draft)}
            className="p-1 rounded hover:bg-gray-100 text-charcoal-500"
            aria-label="Añadir"
          >
            <Plus size={14} />
          </button>
        )}
      </div>

      {showDropdown && pos && createPortal(
        <div
          ref={popRef}
          data-testid={testid ? `${testid}-dropdown` : undefined}
          style={{
            position: 'fixed',
            left: pos.left,
            width: pos.width,
            ...(pos.openUp ? { bottom: window.innerHeight - pos.top } : { top: pos.top }),
            maxHeight: 256,
            overscrollBehavior: 'contain',
          }}
          className="z-[200] overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-xl py-1"
          onWheel={(e) => e.stopPropagation()}
        >
          {filtered.map((opt) => (
            <button
              key={opt}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); addOne(opt); }}
              data-testid={testid ? `${testid}-option-${String(opt).replace(/\W+/g, '-').toLowerCase()}` : undefined}
              className="w-full text-left px-3 py-1.5 text-sm text-charcoal-700 hover:bg-gray-100"
            >
              {opt}
            </button>
          ))}
          {canCreate && (
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); addOne(draftTrimmed); }}
              data-testid={testid ? `${testid}-create` : undefined}
              className="w-full text-left px-3 py-1.5 text-sm text-cmic-700 font-medium hover:bg-cmic-50 flex items-center gap-1.5 border-t border-gray-100 mt-1 pt-2"
            >
              <Plus size={13} /> Crear «{draftTrimmed}»
            </button>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
